import { Card, Typography, Button } from '@achieve/ascend'
import { Check, ThumbsUp, X } from 'react-feather'
import { Image } from 'components/Image'
import { useEffect } from 'react'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import styles from './QuizFlowResult.module.scss'
import { GradientBadge } from './GradientBadge'

import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import { createExperimentObject } from 'utils/experiments'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'

const QuizFlowResult = function ({ content, onButtonClick, onClose }) {
  // for fire and forget events
  const { fireAndForget } = useTriggerEvents()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const PFQBannerExperiment = useFeatureFlag(
    FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)

  useEffect(() => {
    // Disable scrolling when the card is displayed
    document.body.style.overflow = 'hidden'

    // Result Loaded/Displaying event
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'PFQ Result',
      track_event: 'product_fit_quiz_result_loaded',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    // Re-enable scrolling when the component is unmounted
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [PFQBannerExperiment, fireAndForget, siteEnv])

  return (
    <div className={styles['result-container']}>
      <Card className={styles['result-card']} variant="elevation">
        <div className={styles['clickable-container']}>
          <TrackingWrapper
            track={{
              event_type: 'ui_click',
              event_action: 'ui_click',
              nav_link_section: 'PFQ Result',
              track_event: 'product_fit_quiz_result_close',
            }}
            onClick={onClose}
          >
            <div
              style={{
                cursor: 'pointer',
                minWidth: '24px',
                minHeight: '24px',
                marginLeft: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <X
                size={20}
                color={'black'}
                priority={false}
                aria-hidden={true}
                alt="Close button"
                data-testid="pfq-x-icon"
              />
            </div>
          </TrackingWrapper>
        </div>
        <div className={styles['content-container']}>
          <div className={styles['spot-icon-container']}>
            <Image
              src={content.spot}
              alt="Result Icon"
              width={90}
              height={90}
              priority={false}
              aria-hidden={true}
            />
          </div>
          <div className={styles['badge-container']}>
            <GradientBadge width={110} height={33}>
              <Typography variant="labelButtonSm" color={'#3300FF'}>
                Your Results
              </Typography>
            </GradientBadge>
          </div>
          <Typography variant={isMobile ? 'headingSm' : 'headingMd'} style={{ marginTop: '23px' }}>
            {content.heading}
          </Typography>
          <div style={{ marginTop: '23px' }}>
            {content.bullets.map((bullet, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: index < content.bullets.length - 1 ? '12px' : '0',
                }}
              >
                <Check size={20} style={{ color: '#6DCFE9', flexShrink: 0 }} />
                <Typography
                  variant={isMobile ? 'bodySm' : 'bodyMd'}
                  className="text-content-informational"
                  style={{
                    marginLeft: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {bullet}
                </Typography>
              </div>
            ))}
          </div>
          <div className={styles['button-container']}>
            <TrackingWrapper
              track={{
                event_type: 'button_click',
                event_action: 'button_click',
                nav_link_section: 'PFQ Result',
                track_event: 'product_fit_quiz_result_cta',
              }}
              onClick={() => {
                onButtonClick({ flowRedirectPath: content.flowRedirectPath })
              }}
            >
              <Button color="primary" variant="contained" fullWidth>
                <Typography variant="labelButtonMd">{content.ctaButton}</Typography>
              </Button>
            </TrackingWrapper>
          </div>
          <div className={styles['disclaimer-container']}>
            <ThumbsUp size={16} style={{ color: '#5C708A' }} />
            <Typography
              variant="bodyXs"
              className="text-content-informational"
              style={{
                marginLeft: '8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {content.footer}
            </Typography>
          </div>
          <div className={styles['footer-container']}>
            {content.footerSpot && (
              <>
                <Image
                  src={content.footerSpot}
                  alt="Footer Icon"
                  width={43}
                  height={43}
                  priority={false}
                  aria-hidden={true}
                />
                <div
                  style={{
                    width: '1px',
                    height: '93%',
                    marginLeft: '21px',
                    marginRight: '21px',
                    backgroundColor: '#C0CBD8',
                  }}
                />
              </>
            )}
            <Image
              src="/multi-site-assets/trustpilot-score.svg"
              alt="Result Icon"
              width={178}
              height={42}
              priority={false}
              aria-hidden={true}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}

export { QuizFlowResult }
